import Uppy, { UppyFile } from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import { useCallback, useEffect, useRef, useState } from "react";
import { FileProgress, UploadResult, FileProgressStage, UploadError, UploadBatch } from "../types";
import { apiFetch, apiFetchNoResponseRequired } from "../utilities/api";
import { uploadBatchService } from "./uploadBatchService";
import { allowedFileTypesToUpload } from "../constants/upload";
import { UPLOAD_OPTIONS } from "../constants/common";

/**
 * Sanitize filename for S3 storage
 * Replaces all non-alphanumeric characters (except dots and hyphens) with underscores
 * This prevents URL encoding issues with CloudFront and MediaConvert
 */
function sanitizeFilename(filename: string): string {
  // Replace all non-alphanumeric characters (except dots and hyphens) with underscores
  // This handles: spaces, +, (, ), [, ], #, &, etc.
  let sanitized = filename.replace(/[^a-zA-Z0-9._-]/g, "_");

  // Remove leading underscores and dots (e.g., "__test.jpg" → "test.jpg", ".mp4" → "mp4")
  sanitized = sanitized.replace(/^[_.]+/, "");

  // Fallback if filename is empty after sanitization
  if (!sanitized) {
    sanitized = "upload";
  }

  // Normalize file extension to lowercase
  const lastDotIndex = sanitized.lastIndexOf(".");
  if (lastDotIndex > 0) {
    const name = sanitized.substring(0, lastDotIndex);
    const ext = sanitized.substring(lastDotIndex).toLowerCase();
    sanitized = name + ext;
  }

  return sanitized;
}

/**
 * AWS S3 Upload Service
 *
 * This service handles file uploads using Uppy with automatic method selection:
 * - Small files (≤threshold): Single-part uploads with signed URLs (fast, simple)
 * - Large files (>threshold): Multipart uploads with batch-signed URLs (parallel, resumable)
 *
 * ⚠️  CHUNK SIZE CONFIGURATION ⚠️
 * The chunk size and multipart threshold are now provided by the backend in the
 * multipart initialization response. This ensures frontend and backend are always aligned.
 *
 * No more hardcoded constants to keep in sync!
 *
 * Flow:
 * 1. Create media record in Django database with metadata
 * 2. Small files: Get signed URL for direct S3 upload
 *    Large files: Initialize multipart upload - backend returns calculation details
 * 3. Configure Uppy with backend's chunk size settings
 * 4. Upload file(s) directly to S3 using signed URL(s)
 * 5. Update Django record with completion status
 *
 * Benefits:
 * - Automatic method selection based on file size
 * - Parallelism and resumability for large files
 * - Simple and reliable for small files
 * - Efficient batch URL generation (avoiding N API calls per chunk)
 *
 * 1. Create media record in Django database with metadata (name, description, relationships)
 *    - This gives us a database record before the file exists in S3
 *    - Django returns a signed URL for S3 upload
 *
 * 2. Use signed URL to upload file directly to S3
 *    - Client -> S3 direct upload (more efficient than going through our server)
 *
 * 3. Update Django record with S3 upload status and details
 *    - Marks upload as complete
 *    - Stores S3 location details
 *
 * While this might seem like a violation of separation of concerns, the Django interaction
 * is necessary here because:
 * - We need the media record to exist before S3 upload
 * - The signed URL must come from our backend for security
 * - This ensures metadata consistency between database and S3
 *
 * @module awsUploadService
 */

// Define metadata interface that extends Uppy's internal metadata
interface UppyMeta extends Record<string, any> {
  jobId: string;
  fileId: string;
  relationships?: Record<string, any>;
  name?: string;
  description?: string;
  exif_metadata?: Record<string, any> | null;
  geometry?: { type: "Point"; coordinates: [number, number] } | null;
}

// Update the UppyError interface
interface UppyError {
  message?: string;
  request?: {
    status?: number;
    responseText?: string;
    responseURL?: string;
  };
  response?: {
    status?: number;
    statusText?: string;
  };
}

// Default fallback values (only used if backend doesn't provide them)
const FALLBACK_MULTIPART_THRESHOLD = 5 * 1024 * 1024; // 5MB
const FALLBACK_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * Store backend configuration globally to avoid re-fetching
 */
let backendConfig: {
  multipartThreshold: number;
  chunkSize: number;
} | null = null;

/**
 * AWS S3 Upload Service
 *
 * Current Implementation:
 * - Uses single-part uploads with signed URLs for files up to 200MB
 *
 * Future Considerations for Large Files (>200MB):
 * To implement multipart uploads, we would need to:
 * 1. Add backend endpoints for multipart operations (init, sign, complete)
 * 2. Configure S3 CORS for multipart
 * 3. See: https://uppy.io/docs/aws-s3-multipart/
 */

interface CancelState {
  cancelled: boolean;
}

interface CreateUppyOptions {
  // Lets the plugin closures below read the latest cancel state. A media-create
  // request can resolve AFTER the user clicks cancel (it was already in flight),
  // so relying solely on a synchronous snapshot at cancel-time misses it — this
  // lets each request self-cleanup as soon as it learns the upload was cancelled.
  getCancelState?: () => CancelState;
  // Reports mediaId/uploadId as soon as each is known, keyed by frontendId.
  // cancelUpload reads this instead of uppy.getFiles() — Uppy's internal file
  // objects can be swapped out by its own state updates, so mutations made
  // directly on file.meta inside these closures aren't reliably visible from
  // outside; this ref-backed side-channel is.
  onMediaRecordUpdate?: (frontendId: string | undefined, patch: { mediaId?: number; uploadId?: string }) => void;
}

// Best-effort delete used by the self-cleanup checks below — failures are logged,
// not thrown, since the upload is already being abandoned at that point.
function deleteMediaBestEffort(mediaId: number | string) {
  apiFetchNoResponseRequired(`${process.env.REACT_APP_API_ROOT}api/v2/media/${mediaId}/`, { method: "DELETE" }).catch((err) =>
    console.error(`Failed to delete partial media ${mediaId} after cancel:`, err)
  );
}

// Create a function to initialize Uppy with AWS S3 configuration
export const createUppy = (options: CreateUppyOptions = {}) => {
  const { getCancelState, onMediaRecordUpdate } = options;

  return new Uppy({
    debug: process.env.NODE_ENV === "development",
    autoProceed: false,
    allowMultipleUploadBatches: true,
    restrictions: {
      maxFileSize: UPLOAD_OPTIONS?.MAX_FILE_SIZE || 4000 * 1024 * 1024, // 4Gb
      allowedFileTypes: allowedFileTypesToUpload as unknown as string[]
    }
  }).use(AwsS3, {
    getUploadParameters: async (file) => {
      // Sanitize filename for S3 storage to prevent URL encoding issues
      const sanitizedFilename = sanitizeFilename(file.name || "upload");

      // Create media entry first to get signed URL
      const mediaResponse = await apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/?return_signed_url=true`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: file.meta.displayName || file.meta.name || file.name,
          description: file.meta.description,
          media_type: file.type,
          file_type: file.type,
          file_size: file.size,
          original_file: {
            filename: sanitizedFilename,
            original_filename: file.name,
            type: file.type,
            size: `${Math.round((file.size ?? 0) / 1024)}kb`
          },
          upload_details: {
            status: "pending",
            batch_id: file.meta.batch_id,
            upload_method: "signed_url"
          },
          exif_metadata: file.meta.exif_metadata ?? null,
          geometry: file.meta.geometry ?? null
        })
      });

      // Store media ID in file meta for later use
      file.meta.mediaId = mediaResponse.id;
      onMediaRecordUpdate?.(file.meta.frontendId as string | undefined, { mediaId: mediaResponse.id });

      // The cancel button was clicked while this create-media request was still
      // in flight — the record now exists server-side even though cancelUpload's
      // snapshot (taken before this resolved) never saw it. Clean it up here.
      if (getCancelState?.()?.cancelled) {
        deleteMediaBestEffort(mediaResponse.id);
        throw new Error("Upload cancelled");
      }

      // Extract signed URL from the response
      const signedUrl = mediaResponse.upload_details?.signed_url;
      if (!signedUrl) {
        throw new Error("No signed URL returned from server");
      }

      return {
        method: "PUT",
        url: signedUrl,
        fields: {},
        headers: {
          "Content-Type": file.type
        }
      };
    },

    shouldUseMultipart: (file) => {
      const size = file.size ?? 0;
      const threshold = backendConfig?.multipartThreshold ?? FALLBACK_MULTIPART_THRESHOLD;
      return size > threshold;
    },

    // ⚠️  DYNAMIC CONFIGURATION: Chunk size determined by backend response
    getChunkSize: (file) => {
      return backendConfig?.chunkSize ?? FALLBACK_CHUNK_SIZE;
    },

    createMultipartUpload: async (file) => {
      try {
        // Sanitize filename for S3 storage
        const sanitizedFilename = sanitizeFilename(file.name || "upload");

        // Create media entry first
        const mediaResponse = await apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: file.meta.displayName || file.meta.name || file.name,
            description: file.meta.description,
            media_type: file.type,
            file_type: file.type,
            file_size: file.size,
            original_file: {
              filename: sanitizedFilename,
              original_filename: file.name,
              type: file.type,
              size: `${Math.round((file.size ?? 0) / 1024)}kb`
            },
            upload_details: {
              status: "pending",
              batch_id: file.meta.batch_id,
              upload_method: "s3_multipart"
            },
            exif_metadata: file.meta.exif_metadata ?? null,
            geometry: file.meta.geometry ?? null
          })
        });

        // Store media ID in file meta for later use
        file.meta.mediaId = mediaResponse.id;
        onMediaRecordUpdate?.(file.meta.frontendId as string | undefined, { mediaId: mediaResponse.id });

        // Cancelled while this create-media request was in flight — see the
        // matching check in getUploadParameters for why this can't be left to
        // cancelUpload's snapshot alone.
        if (getCancelState?.()?.cancelled) {
          deleteMediaBestEffort(mediaResponse.id);
          throw new Error("Upload cancelled");
        }

        // ⚠️  CRITICAL: Let backend calculate parts and provide configuration
        const fileSize = file.size ?? 0;

        // First call - frontend sends its guess, backend validates and responds with config
        const result = await apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/multipart/initialize/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            filename: sanitizedFilename, // Use sanitized filename for S3 key
            fileType: file.type,
            fileSize: fileSize,
            parts: Math.ceil(fileSize / (backendConfig?.chunkSize ?? FALLBACK_CHUNK_SIZE)), // Frontend's best guess
            media_id: mediaResponse.id
          })
        });

        // ⚠️  CRITICAL: Backend returns calculation details - use as single source of truth
        const calculation = result.calculation;
        if (!calculation) {
          throw new Error("Backend did not return calculation details");
        }

        // Store backend configuration for future uploads in this session
        backendConfig = {
          multipartThreshold: calculation.multipart_threshold,
          chunkSize: calculation.chunk_size
        };

        // Validate we have enough URLs
        if (result.urls.length < calculation.parts_needed) {
          throw new Error(
            `Insufficient pre-signed URLs! Need ${calculation.parts_needed} parts but received ${result.urls.length} URLs. ` +
              `File size: ${calculation.file_size} bytes, chunk size: ${calculation.chunk_size} bytes`
          );
        }

        // Store the URLs for later use in signPart
        file.meta.preSignedUrls = result.urls;
        file.meta.calculation = calculation; // Store calculation for debugging

        const key = `org_${mediaResponse.organisation}/${mediaResponse.id}/${sanitizedFilename}`;

        // Persist the multipart identifiers so a cancel can abort the S3 upload
        // directly (and await it) before deleting the media record.
        file.meta.uploadId = result.upload_id;
        file.meta.multipartKey = key;
        onMediaRecordUpdate?.(file.meta.frontendId as string | undefined, { uploadId: result.upload_id });

        // Cancelled while the multipart/initialize request was in flight — abort
        // the S3 multipart upload we just created before deleting the media
        // record, same ordering cancelUpload uses for its own snapshot.
        if (getCancelState?.()?.cancelled) {
          try {
            await abortMultipartUploadById(result.upload_id);
          } catch (abortErr) {
            console.error("Failed to abort multipart upload after cancel:", abortErr);
          }
          deleteMediaBestEffort(mediaResponse.id);
          throw new Error("Upload cancelled");
        }

        return {
          uploadId: result.upload_id,
          key
        };
      } catch (error) {
        console.error("Failed to create multipart upload:", error);
        throw error;
      }
    },

    listParts: async (file, { uploadId, key, signal }) => {
      try {
        // Since we get all URLs upfront, we don't need to list parts individually
        return [];
      } catch (error) {
        console.error("Failed to list parts:", error);
        throw error;
      }
    },

    signPart: async (file, { partNumber, uploadId, key, signal }) => {
      try {
        // Use pre-signed URLs from initialization instead of individual API calls
        const preSignedUrls = file.meta.preSignedUrls;
        if (!preSignedUrls || !Array.isArray(preSignedUrls)) {
          throw new Error("Pre-signed URLs not available");
        }

        const url = preSignedUrls[partNumber - 1]; // Array is 0-indexed, partNumber is 1-indexed
        if (!url) {
          console.error(`[UPLOAD_SERVICE] No pre-signed URL for part ${partNumber}. Available URLs: ${preSignedUrls.length}`);
          throw new Error(`No pre-signed URL for part ${partNumber}`);
        }

        return {
          url: url,
          headers: {}
        };
      } catch (error) {
        console.error("Failed to get pre-signed URL for part:", error);
        throw error;
      }
    },

    abortMultipartUpload: async (file, { uploadId, key, signal }) => {
      try {
        if (!uploadId || !key) {
          throw new Error("Missing uploadId or key");
        }

        await apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/multipart/abort/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ upload_id: uploadId }),
          signal
        });
      } catch (error) {
        console.error("Failed to abort multipart upload:", error);
        throw error;
      }
    },

    completeMultipartUpload: async (file, { uploadId, key, parts, signal }) => {
      try {
        if (!uploadId || !key || !parts) {
          throw new Error("Missing required parameters");
        }

        // Filter parts to only include ETag and PartNumber (AWS requirement)
        const filteredParts = parts.map((part) => ({
          ETag: part.ETag, // Use only the uppercase ETag property
          PartNumber: part.PartNumber
        }));

        const result = await apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/multipart/complete/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            upload_id: uploadId,
            parts: filteredParts, // Use filtered parts instead of raw parts
            filename: sanitizeFilename(file.name || "upload") // Sanitize for consistency
          }),
          signal
        });
        return {
          location: result.location
        };
      } catch (error) {
        console.error("Failed to complete multipart upload:", error);
        throw error;
      }
    }
  });
};

/**
 * Abort an in-progress S3 multipart upload by its upload id.
 *
 * Tells the backend to abort the multipart upload so S3 discards the parts that
 * were uploaded so far. Best-effort: the upload may already have been aborted by
 * Uppy's own teardown, so callers should tolerate a rejection here.
 */
async function abortMultipartUploadById(uploadId: string): Promise<void> {
  await apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/multipart/abort/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ upload_id: uploadId })
  });
}

// Hook for managing uploads
export const useS3Upload = () => {
  // Read by the Uppy plugin closures (via getCancelState) so a create-media
  // request that resolves AFTER cancel was clicked can self-cleanup instead of
  // being missed by cancelUpload's synchronous snapshot. Also doubles as the
  // "did the user cancel" flag that uploadFiles checks.
  const cancelStateRef = useRef<CancelState>({ cancelled: false });

  // Source of truth for "which media record belongs to which file", populated
  // directly by the Uppy plugin callbacks via onMediaRecordUpdate. cancelUpload
  // reads this instead of uppy.getFiles() — Uppy's own state updates can replace
  // its internal file objects, so meta mutated inside plugin closures isn't
  // reliably visible to code outside them (this was the cause of the delete API
  // never firing on cancel).
  const mediaRecordsRef = useRef<Map<string, { mediaId: number; uploadId?: string }>>(new Map());

  const [uppy] = useState(() =>
    createUppy({
      getCancelState: () => cancelStateRef.current,
      onMediaRecordUpdate: (frontendId, patch) => {
        if (!frontendId) return;
        const existing = mediaRecordsRef.current.get(frontendId);
        mediaRecordsRef.current.set(frontendId, { ...existing, ...patch } as { mediaId: number; uploadId?: string });
      }
    })
  );
  const [progress, setProgress] = useState<Record<string, FileProgress>>({});
  const [errors, setErrors] = useState<UploadError[]>([]);

  // The batch currently being uploaded — needed so a cancel can mark it failed.
  const currentBatchRef = useRef<UploadBatch | null>(null);
  // Mirror of progress so cancelUpload can read the latest stages synchronously.
  const progressRef = useRef(progress);
  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    if (!uppy) return;

    const handleProgress = (
      file: UppyFile<Record<string, any>, Record<string, any>>,
      progressData: { bytesUploaded: number; bytesTotal: number | null }
    ) => {
      // Use frontend ID for consistent progress tracking
      const frontendId = file.meta?.frontendId;
      if (!frontendId || !progressData.bytesTotal) {
        return;
      }

      const progressPercent = Math.round((progressData.bytesUploaded / progressData.bytesTotal!) * 100);

      setProgress((prev) => {
        const newProgress = {
          ...prev,
          [frontendId]: {
            stage: "uploading" as FileProgressStage,
            completed: progressData.bytesUploaded,
            total: progressData.bytesTotal!,
            progress: progressPercent
          }
        };
        return newProgress;
      });
    };

    const handleUploadStart = (file: UppyFile<Record<string, any>, Record<string, any>>) => {
      // Use frontend ID for consistent progress tracking
      const frontendId = file.meta?.frontendId;
      if (!frontendId) {
        return;
      }

      setProgress((prev) => {
        const newProgress = {
          ...prev,
          [frontendId]: {
            stage: "presigning" as FileProgressStage,
            completed: 0,
            total: file.size || 0,
            progress: 0
          }
        };
        return newProgress;
      });
    };

    const handleUploadError = (file: UppyFile<Record<string, any>, Record<string, any>>, error: Error) => {
      const frontendId = file.meta?.frontendId as string | undefined;
      if (frontendId) {
        setProgress((prev) => ({
          ...prev,
          [frontendId]: {
            ...prev[frontendId],
            stage: "failed"
          }
        }));
      }

      setErrors((prev) => [
        ...prev,
        {
          file: file.name || "Unknown file",
          stage: "upload",
          error: error.message || "Upload failed"
        }
      ]);
    };

    uppy.on("upload-progress", handleProgress as any);
    uppy.on("file-added", handleUploadStart as any);
    uppy.on("upload-error", handleUploadError as any);

    return () => {
      uppy.off("upload-progress", handleProgress as any);
      uppy.off("file-added", handleUploadStart as any);
      uppy.off("upload-error", handleUploadError as any);
    };
  }, [uppy]);

  const uploadFiles = useCallback(
    async (
      files: File[],
      metadata: Array<{
        id: string | number;
        name: string;
        description: string;
        relationships?: Record<string, any>;
        exif_metadata?: Record<string, any> | null;
        geometry?: { type: "Point"; coordinates: [number, number] } | null;
      }>
    ) => {
      try {
        uppy.cancelAll();
        cancelStateRef.current = { cancelled: false };
        mediaRecordsRef.current.clear();
        setProgress({});
        setErrors([]); // Clear previous errors

        const batch = await uploadBatchService.createBatch(files);
        currentBatchRef.current = batch;
        const startTime = Date.now();

        // Add files to Uppy
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const fileMetadata = metadata[i];

          setProgress((prev) => ({
            ...prev,
            [fileMetadata.id]: {
              stage: "pending" as FileProgressStage,
              completed: 0,
              total: file.size,
              progress: 0
            }
          }));

          const uppyFileId = uppy.addFile({
            name: file.name,
            type: file.type,
            data: file,
            meta: {
              frontendId: fileMetadata.id, // Store frontend ID for progress tracking
              displayName: fileMetadata.name,
              name: fileMetadata.name,
              description: fileMetadata.description,
              relationships: fileMetadata.relationships,
              batch_id: batch.id,
              exif_metadata: fileMetadata.exif_metadata ?? null,
              geometry: fileMetadata.geometry ?? null
            }
          });
        }
        const result = await uppy.upload();

        // User cancelled while uploading — cancelUpload owns the batch/media
        // cleanup, so skip the success/failure bookkeeping below.
        if (cancelStateRef.current.cancelled) {
          return [];
        }

        if (!result?.successful || !result?.failed) {
          const errorDetails = {
            type: "upload_failure",
            message: "Upload failed: No result returned",
            duration: Date.now() - startTime
          };

          await uploadBatchService.updateBatchStatus(batch.id, "failed", errorDetails);

          throw new Error(errorDetails.message);
        }

        if (result.failed.length > 0) {
          const failedUploads = result.failed.map((f) => {
            let errorMessage = "Unknown error";
            let errorType = "UNKNOWN_ERROR";

            // Log the raw error for debugging
            console.debug("Raw Uppy S3 error:", f.error);

            if (typeof f.error === "string") {
              errorMessage = f.error;
              // Common S3 error patterns in string format
              if (f.error.includes("403")) {
                errorType = "PERMISSION_ERROR";
                errorMessage = "Access denied - CORS settings need to be updated";
              } else if (f.error.includes("Network Error")) {
                errorType = "NETWORK_ERROR";
                errorMessage = "Network connection error";
              }
            } else if (typeof f.error === "object" && f.error !== null) {
              const error = f.error as UppyError;

              // Handle S3 specific errors
              if (error.response?.status === 403) {
                errorType = "PERMISSION_ERROR";
                errorMessage = "Access denied - Check CORS configuration";
              } else if (error.request?.status) {
                errorType = "REQUEST_ERROR";
                errorMessage = `Request failed (${error.request.status})`;

                try {
                  if (error.request.responseText) {
                    const responseData = JSON.parse(error.request.responseText);
                    if (responseData.message) {
                      errorMessage = `S3 Error: ${responseData.message}`;
                    }
                  }
                } catch (e) {
                  // If we can't parse the response, use the raw text
                  if (error.request.responseText) {
                    errorMessage = `S3 Error: ${error.request.responseText}`;
                  }
                }
              } else if (error.message) {
                errorMessage = error.message;

                // S3 specific error patterns
                if (error.message.includes("ETag")) {
                  errorType = "ETAG_ERROR";
                  errorMessage = "File verification failed";
                } else if (error.message.includes("timeout")) {
                  errorType = "TIMEOUT_ERROR";
                  errorMessage = "Upload timed out - try a smaller file or check your connection";
                } else if (error.message.includes("CORS")) {
                  errorType = "CORS_ERROR";
                  errorMessage = "CORS configuration error - contact support";
                }
              }
            }

            return {
              file: f.name || "Unknown file",
              error: errorMessage,
              error_type: errorType,
              size: f.size || 0,
              type: f.type || "unknown",
              details: {
                response_status: (f.error as UppyError)?.response?.status,
                response_url: (f.error as UppyError)?.request?.responseURL,
                raw_error: f.error
              }
            };
          });

          const errorDetails = {
            type: "upload_failure",
            message: failedUploads.map((f) => `${f.file}: ${f.error}`).join(", "),
            duration: Date.now() - startTime,
            failed_files: failedUploads,
            error_summary: `Upload failed after ${Math.round((Date.now() - startTime) / 1000)}s with ${failedUploads.length} errors`
          };

          await uploadBatchService.updateBatchStatus(batch.id, "failed", errorDetails);

          throw new Error(`Upload failed: ${failedUploads.map((f) => `${f.file} (${f.error_type}: ${f.error})`).join(", ")}`);
        }

        // Update media entries with completed status and get final results
        const mediaResults = await Promise.all(
          result.successful.map(async (file) => {
            // Determine upload method based on whether multipart was used
            const wasMultipart = file.meta?.preSignedUrls || file.meta?.uploadId;
            const uploadMethod = wasMultipart ? "s3_multipart" : "signed_url";

            // Update media entry with completed status and get response
            const formData = new FormData();

            formData.append(
              "upload_details",
              JSON.stringify({
                status: "completed",
                batch_id: batch.id,
                upload_method: uploadMethod
              })
            );

            formData.append(
              "remote_details",
              JSON.stringify({
                service: "s3",
                url: file.uploadURL || "",
                thumbnail: "",
                id: String(file.meta.mediaId),
                mime: file.type,
                bucket: process.env.REACT_APP_S3_BUCKET!,
                key: file.meta.key || ""
              })
            );

            const mediaResponse = await apiFetch(`${process.env.REACT_APP_API_ROOT}api/v2/media/${file.meta.mediaId}/`, {
              method: "PATCH",
              body: formData // Remove Content-Type header to let browser set it
            });

            // Update progress to completed
            const frontendId = file.meta?.frontendId as string | undefined;
            if (frontendId) {
              setProgress((prev) => ({
                ...prev,
                [frontendId]: {
                  ...prev[frontendId],
                  stage: "completed",
                  progress: 100
                }
              }));
            }

            return {
              file: file.data as File,
              uploadType: "s3" as const,
              remoteDetails: mediaResponse.remote_details,
              mediaId: file.meta.mediaId as number,
              frontendId: file.meta.frontendId as string | undefined
            };
          })
        );

        // Update batch status to completed
        await uploadBatchService.updateBatchStatus(batch.id, "completed");
        currentBatchRef.current = null;

        return mediaResults;
      } catch (error) {
        // A cancel tears down the in-flight uploads, which can surface here as a
        // thrown error — swallow it since cancelUpload handles cleanup/messaging.
        if (cancelStateRef.current.cancelled) {
          return [];
        }
        console.error("Upload failed:", error);
        throw error;
      }
    },
    [uppy]
  );

  /**
   * Cancel the in-flight upload batch.
   *
   * Stops every running/queued upload, deletes the media records that were
   * created for files that hadn't finished uploading yet (a partial/incomplete
   * record left behind would otherwise show up as a broken entry in the media
   * list), and marks the batch as failed so the backend doesn't leave it
   * dangling as in_progress.
   *
   * Files whose create-media request was still in flight when this is called
   * aren't in mediaRecordsRef with a mediaId yet — those self-cleanup inside the
   * getUploadParameters/createMultipartUpload closures once their request
   * resolves and they see cancelStateRef.current.cancelled.
   *
   * @returns the number of partial media records that were successfully deleted.
   */
  const cancelUpload = useCallback(async (): Promise<{ deletedCount: number }> => {
    // Set before cancelAll() so any create-media request already in flight
    // sees this as soon as it resolves and can self-cleanup (see createUppy).
    cancelStateRef.current = { cancelled: true };
    const batch = currentBatchRef.current;

    // Snapshot from mediaRecordsRef (not uppy.getFiles()) BEFORE cancelAll() —
    // Uppy's own state updates can replace its internal file objects, so this
    // ref-backed side-channel is the only reliable read of mediaId/uploadId. A
    // record whose progress never reached "completed" is a partial; uploadId is
    // present only for files that began a multipart upload.
    const partialFiles = Array.from(mediaRecordsRef.current.entries())
      .filter(([frontendId]) => progressRef.current[frontendId]?.stage !== "completed")
      .map(([, record]) => record);

    // Stop everything in flight / queued.
    uppy.cancelAll();

    // Abort the S3 multipart uploads first so S3 discards the uploaded parts,
    // then delete the media records. Aborts are best-effort (cancelAll may have
    // already torn them down), so failures here must not block the delete.
    await Promise.allSettled(partialFiles.filter((f) => f.uploadId).map((f) => abortMultipartUploadById(f.uploadId as string)));

    let deletedCount = 0;
    if (partialFiles.length > 0) {
      const results = await Promise.allSettled(
        partialFiles.map((f) =>
          apiFetchNoResponseRequired(`${process.env.REACT_APP_API_ROOT}api/v2/media/${f.mediaId}/`, { method: "DELETE" })
        )
      );
      deletedCount = results.filter((r) => r.status === "fulfilled").length;
    }

    if (batch?.id) {
      try {
        await uploadBatchService.updateBatchStatus(batch.id, "failed", {
          type: "cancelled",
          message: "Upload cancelled by user",
          error_summary: `Cancelled — ${deletedCount} partial item(s) deleted`
        });
      } catch (err) {
        console.error("Failed to update batch status after cancel:", err);
      }
    }

    currentBatchRef.current = null;
    mediaRecordsRef.current.clear();
    setProgress({});
    setErrors([]);

    return { deletedCount };
  }, [uppy]);

  return { uppy, uploadFiles, cancelUpload, progress, errors };
};
